import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const DISMISS_KEY = "ibeflix_install_dismissed";
const TRACKED_KEY = "ibeflix_install_tracked";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function trackInstall() {
  if (localStorage.getItem(TRACKED_KEY)) return;
  localStorage.setItem(TRACKED_KEY, "1");
  fetch(`${API}/pwa/install`, { method: "POST" }).catch(() => {});
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) { trackInstall(); return; }
    if (localStorage.getItem(DISMISS_KEY)) return;

    // Android / desktop Chromium: capture the native prompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual "Add to Home Screen" hint
    let iosTimer;
    if (isIOS() && !window.navigator.standalone) {
      iosTimer = setTimeout(() => { setIosHint(true); setShow(true); }, 2500);
    }

    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); trackInstall(); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") { localStorage.setItem(DISMISS_KEY, "1"); trackInstall(); }
    } catch (e) { /* prompt closed */ }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 z-[95] sm:left-auto sm:right-4 sm:w-[380px] animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="relative flex items-center gap-3 rounded-2xl bg-[#0A0E27]/95 backdrop-blur-xl border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.6)] p-3.5 pr-10">
        <div className="w-11 h-11 shrink-0 rounded-xl bg-[#060817] border border-white/10 flex items-center justify-center overflow-hidden">
          <img src="/icon-192.png" alt="Ibeflix" className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          {iosHint ? (
            <>
              <p className="text-sm font-semibold text-[#F1F1F5] leading-tight">
                Add <BrandLogo className="text-sm align-baseline" /> to your Home Screen
              </p>
              <p className="text-xs text-[#A0A0AB] mt-0.5 flex items-center gap-1 flex-wrap">
                Tap <Share className="w-3.5 h-3.5 inline text-[#00D9FF]" /> then
                <span className="inline-flex items-center gap-0.5"><Plus className="w-3 h-3" /> Add to Home Screen</span>
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-[#F1F1F5] leading-tight">
                Install <BrandLogo className="text-sm align-baseline" />
              </p>
              <p className="text-xs text-[#A0A0AB] mt-0.5">One tap — launch your watch parties like a native app.</p>
            </>
          )}
        </div>
        {!iosHint && (
          <button
            data-testid="install-app-button"
            onClick={install}
            className="shrink-0 flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#00D9FF] text-navy-ink text-sm font-bold hover:bg-[#33E4FF] transition-colors active:scale-95 shadow-[0_2px_12px_rgba(230,51,41,0.45)]"
          >
            <Download className="w-4 h-4" /> Install
          </button>
        )}
        <button
          data-testid="install-dismiss-button"
          onClick={dismiss}
          title="Not now"
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
